/**
 * AIF-01 — Import classes dialog (§11.3).
 *
 * Upload a file OR paste text; the client-side parser (`parseClassList`)
 * previews the index↔name rows with a per-row action (created / updated /
 * unchanged) computed against the currently loaded classes; the footer shows
 * "Σ create N · update N · unchanged N". Confirm POSTs the RAW content (+
 * detected format) — the server is authoritative and returns its own counts,
 * which we surface on success.
 */
import { useMemo, useRef, useState, type ReactElement } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import type {
  AiFindingModelClass,
  ImportAiModelClassesResponse,
} from "@aeriosiq/types";
import { useImportAiModelClassesMutation } from "./aiModelQueries.js";
import {
  ParseClassListError,
  parseClassList,
  type ParseClassListResult,
} from "./parseClassList.js";

type PreviewAction = "created" | "updated" | "unchanged";

interface PreviewRow {
  index: number;
  name: string;
  action: PreviewAction;
}

export interface ImportClassesDialogProps {
  open: boolean;
  modelId: string;
  modelName: string;
  /** Currently loaded classes — used to compute create/update/unchanged. */
  existing: AiFindingModelClass[];
  onClose: () => void;
  onImported: () => void;
}

const FORMAT_LABEL: Record<ParseClassListResult["format"], string> = {
  classes_txt: "classes.txt",
  data_yaml: "YOLO data.yaml `names:`",
};

export function ImportClassesDialog({
  open,
  modelId,
  modelName,
  existing,
  onClose,
  onImported,
}: ImportClassesDialogProps): ReactElement {
  const [source, setSource] = useState<"upload" | "paste">("upload");
  const [content, setContent] = useState("");
  const [fileName, setFileName] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const importMut = useImportAiModelClassesMutation(modelId);
  const [result, setResult] = useState<ImportAiModelClassesResponse | null>(
    null,
  );

  // Parse + compute the preview. A parse error is surfaced inline; an empty
  // textarea simply shows nothing.
  const parsed = useMemo<
    | { ok: true; rows: PreviewRow[]; format: ParseClassListResult["format"] }
    | { ok: false; error: string }
    | null
  >(() => {
    if (content.trim().length === 0) return null;
    try {
      const { rows, format } = parseClassList(content);
      const byIndex = new Map(existing.map((c) => [c.classIndex, c]));
      const preview: PreviewRow[] = rows.map((r) => {
        const cur = byIndex.get(r.index);
        let action: PreviewAction;
        if (!cur) action = "created";
        else if (cur.className !== r.name) action = "updated";
        else action = "unchanged";
        return { index: r.index, name: r.name, action };
      });
      return { ok: true, rows: preview, format };
    } catch (e) {
      const msg =
        e instanceof ParseClassListError
          ? e.message
          : "Could not parse the file.";
      return { ok: false, error: msg };
    }
  }, [content, existing]);

  const counts = useMemo(() => {
    if (!parsed?.ok) {
      return { created: 0, updated: 0, unchanged: 0 };
    }
    return parsed.rows.reduce<Record<PreviewAction, number>>(
      (acc, r) => {
        acc[r.action] += 1;
        return acc;
      },
      { created: 0, updated: 0, unchanged: 0 },
    );
  }, [parsed]);

  const canImport =
    parsed != null &&
    parsed.ok &&
    parsed.rows.length > 0 &&
    !importMut.isPending;

  function reset(): void {
    setSource("upload");
    setContent("");
    setFileName(null);
    setResult(null);
    importMut.reset();
  }

  function handleClose(): void {
    reset();
    onClose();
  }

  async function handleFile(file: File): Promise<void> {
    const text = await file.text();
    setContent(text);
    setFileName(file.name);
  }

  function handleImport(): void {
    if (importMut.isPending) return;
    if (parsed === null || !parsed.ok || parsed.rows.length === 0) return;
    importMut.mutate(
      { content, format: parsed.format },
      {
        onSuccess: (res) => {
          setResult(res);
          onImported();
        },
      },
    );
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Import classes — {modelName}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {result ? (
            <Alert severity="success">
              Imported: {result.created} created · {result.updated} updated ·{" "}
              {result.unchanged} unchanged.
            </Alert>
          ) : null}
          {importMut.isError ? (
            <Alert severity="error">
              Import failed. Check the file format and your permissions.
            </Alert>
          ) : null}

          <RadioGroup
            row
            value={source}
            onChange={(e) => {
              setSource(e.target.value as "upload" | "paste");
            }}
          >
            <FormControlLabel
              value="upload"
              control={<Radio size="small" />}
              label="Upload file"
            />
            <FormControlLabel
              value="paste"
              control={<Radio size="small" />}
              label="Paste text"
            />
          </RadioGroup>

          {source === "upload" ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Button
                variant="outlined"
                onClick={() => fileInputRef.current?.click()}
              >
                Browse
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".txt,.yaml,.yml,text/plain"
                aria-label="Class list file"
                style={{ display: "none" }}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) void handleFile(f);
                }}
              />
              <Typography variant="body2" color="text.secondary">
                {fileName ?? "classes.txt or data.yaml"}
              </Typography>
            </Box>
          ) : (
            <TextField
              label="Paste classes.txt / data.yaml"
              value={content}
              onChange={(e) => {
                setContent(e.target.value);
                setFileName(null);
              }}
              fullWidth
              multiline
              minRows={5}
              slotProps={{ htmlInput: { "aria-label": "Class list text" } }}
            />
          )}

          {parsed && !parsed.ok ? (
            <Alert severity="error">{parsed.error}</Alert>
          ) : null}

          {parsed?.ok ? (
            <>
              <Typography variant="body2" color="text.secondary">
                Detected: {FORMAT_LABEL[parsed.format]} — {parsed.rows.length}{" "}
                entries
              </Typography>
              <TableContainer sx={{ maxHeight: 280 }}>
                <Table
                  size="small"
                  stickyHeader
                  aria-label="Class import preview"
                >
                  <TableHead>
                    <TableRow>
                      <TableCell>Idx</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {parsed.rows.map((r) => (
                      <TableRow key={r.index}>
                        <TableCell>{r.index}</TableCell>
                        <TableCell>{r.name}</TableCell>
                        <TableCell>{r.action}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Typography variant="body2" data-testid="import-preview-summary">
                Σ create {counts.created} · update {counts.updated} · unchanged{" "}
                {counts.unchanged}
              </Typography>
            </>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{result ? "Close" : "Cancel"}</Button>
        {!result ? (
          <Button
            variant="contained"
            onClick={handleImport}
            disabled={!canImport}
          >
            {parsed?.ok
              ? `Import ${String(parsed.rows.length)} classes`
              : "Import"}
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
}
